import { useState, useEffect } from 'react';
import axios from 'axios';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Autocomplete from '@mui/material/Autocomplete';
import CategoryMultiSelect from './CategoryMultiSelect';
import { useSnackbar } from 'notistack';
import { useNavigate, useParams } from 'react-router-dom';
import MetaData from '../Layouts/MetaData';
import BackdropLoader from '../Layouts/BackdropLoader';

const ruleTypeLabels = {
    newest: "Newest Arrivals",
    discount: "Best Discounts",
    rating: "Top Rated",
    random: "Random",
    price_low: "Price: Low to High",
    price_high: "Price: High to Low",
    low_stock: "Low Stock (in stock, ascending)",
};

const ProductSectionForm = () => {
    const { id } = useParams();
    const isEditMode = Boolean(id);

    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(isEditMode);
    const [saving, setSaving] = useState(false);

    const [heading, setHeading] = useState("");
    const [headingZh, setHeadingZh] = useState("");
    const [subheading, setSubheading] = useState("");
    const [subheadingZh, setSubheadingZh] = useState("");
    const [sourceType, setSourceType] = useState("rule");
    const [order, setOrder] = useState(0);
    const [limit, setLimit] = useState(12);
    const [active, setActive] = useState(true);
    const [showOnWebsite, setShowOnWebsite] = useState(true);
    const [showOnApp, setShowOnApp] = useState(true);

    const [categories, setCategories] = useState([]); // ids
    const [selectedProducts, setSelectedProducts] = useState([]); // full objects, for the Autocomplete
    const [brand, setBrand] = useState("");
    const [ruleType, setRuleType] = useState("newest");
    const [minDiscountPercent, setMinDiscountPercent] = useState(0);
    const [minRating, setMinRating] = useState(0);

    const [allProducts, setAllProducts] = useState([]);
    const [brandOptions, setBrandOptions] = useState([]);

    useEffect(() => {
        const fetchProducts = async () => {
            try {
                const { data } = await axios.get('/api/v1/admin/products');
                setAllProducts(data.products);
                setBrandOptions([...new Set(data.products.map((p) => p.brand?.name).filter(Boolean))].sort());
            } catch (err) {
                console.error('Failed to load products', err);
            }
        };
        fetchProducts();
    }, []);

    useEffect(() => {
        if (!isEditMode) {
            setLoading(false);
            return;
        }
        const fetchSection = async () => {
            try {
                const { data } = await axios.get('/api/v1/admin/product-sections');
                const section = data.sections.find((s) => s._id === id);
                if (!section) {
                    enqueueSnackbar("Product section not found", { variant: "error" });
                    navigate('/admin/product-sections');
                    return;
                }
                setHeading(section.heading);
                setHeadingZh(section.headingZh || "");
                setSubheading(section.subheading || "");
                setSubheadingZh(section.subheadingZh || "");
                setSourceType(section.sourceType);
                setOrder(section.order || 0);
                setLimit(section.limit || 12);
                setActive(section.active);
                setShowOnWebsite(section.showOnWebsite !== false);
                setShowOnApp(section.showOnApp !== false);
                setCategories((section.categories || []).map((c) => c._id || c));
                setSelectedProducts(section.products || []);
                setBrand(section.brand || "");
                setRuleType(section.rule?.type || "newest");
                setMinDiscountPercent(section.rule?.minDiscountPercent || 0);
                setMinRating(section.rule?.minRating || 0);
            } catch (error) {
                enqueueSnackbar(error?.response?.data?.message || "Failed to load section", { variant: "error" });
            } finally {
                setLoading(false);
            }
        };
        fetchSection();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const submitHandler = async (e) => {
        e.preventDefault();

        if (!heading.trim()) {
            enqueueSnackbar("Please enter a heading", { variant: "warning" });
            return;
        }
        if (sourceType === "category" && categories.length === 0) {
            enqueueSnackbar("Please select at least one category", { variant: "warning" });
            return;
        }
        if (sourceType === "products" && selectedProducts.length === 0) {
            enqueueSnackbar("Please select at least one product", { variant: "warning" });
            return;
        }
        if (sourceType === "brand" && !brand) {
            enqueueSnackbar("Please choose a brand", { variant: "warning" });
            return;
        }

        const payload = {
            heading, headingZh, subheading, subheadingZh, sourceType, order, limit, active,
            showOnWebsite, showOnApp,
            categories: sourceType === "category" ? categories : [],
            products: sourceType === "products" ? selectedProducts.map((p) => p._id) : [],
            brand: sourceType === "brand" ? brand : "",
            rule: sourceType === "rule" ? { type: ruleType, minDiscountPercent, minRating } : undefined,
        };

        setSaving(true);
        try {
            if (isEditMode) {
                await axios.put(`/api/v1/admin/product-section/${id}`, payload);
                enqueueSnackbar("Product section updated", { variant: "success" });
            } else {
                await axios.post('/api/v1/admin/product-section/new', payload);
                enqueueSnackbar("Product section created", { variant: "success" });
            }
            navigate('/admin/product-sections');
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || "Failed to save section", { variant: "error" });
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <BackdropLoader />;

    return (
        <>
            <MetaData title={`Admin: ${isEditMode ? "Edit" : "New"} Product Section | 1313 Mart`} />
            {saving && <BackdropLoader />}

            <form onSubmit={submitHandler} className="flex flex-col gap-4 bg-white rounded-lg shadow p-4 sm:p-6" id="productsectionform">

                <h1 className="text-lg font-medium uppercase">{isEditMode ? "Edit" : "New"} Product Section</h1>

                <div className="flex flex-col sm:flex-row gap-4">
                    <TextField label="Heading" variant="outlined" size="small" required fullWidth value={heading} onChange={(e) => setHeading(e.target.value)} />
                    <TextField label="Heading (中文 — optional)" variant="outlined" size="small" fullWidth value={headingZh} onChange={(e) => setHeadingZh(e.target.value)} />
                    <TextField label="Subheading" variant="outlined" size="small" fullWidth value={subheading} onChange={(e) => setSubheading(e.target.value)} />
                    <TextField label="Subheading (中文 — optional)" variant="outlined" size="small" fullWidth value={subheadingZh} onChange={(e) => setSubheadingZh(e.target.value)} />
                </div>

                <TextField
                    label="Show Products From"
                    select
                    variant="outlined"
                    size="small"
                    value={sourceType}
                    onChange={(e) => setSourceType(e.target.value)}
                    sx={{ maxWidth: 300 }}
                >
                    <MenuItem value="category">A Category (or several)</MenuItem>
                    <MenuItem value="products">Manually Selected Products</MenuItem>
                    <MenuItem value="brand">A Brand</MenuItem>
                    <MenuItem value="rule">An Automatic Rule</MenuItem>
                </TextField>

                {sourceType === "category" && (
                    <CategoryMultiSelect value={categories} onChange={setCategories} label="Categories (select one or more)" />
                )}

                {sourceType === "products" && (
                    <Autocomplete
                        multiple
                        size="small"
                        options={allProducts}
                        getOptionLabel={(opt) => opt.name || ""}
                        isOptionEqualToValue={(opt, val) => opt._id === val._id}
                        value={selectedProducts}
                        onChange={(e, newValue) => setSelectedProducts(newValue)}
                        renderInput={(params) => <TextField {...params} label="Products (in the order they'll appear)" placeholder="Search products..." />}
                    />
                )}

                {sourceType === "brand" && (
                    <TextField label="Brand" select variant="outlined" size="small" value={brand} onChange={(e) => setBrand(e.target.value)} sx={{ maxWidth: 300 }}>
                        {brandOptions.map((b) => <MenuItem value={b} key={b}>{b}</MenuItem>)}
                    </TextField>
                )}

                {sourceType === "rule" && (
                    <div className="flex flex-col sm:flex-row gap-4">
                        <TextField label="Rule" select variant="outlined" size="small" value={ruleType} onChange={(e) => setRuleType(e.target.value)} sx={{ minWidth: 220 }}>
                            {Object.entries(ruleTypeLabels).map(([key, label]) => (
                                <MenuItem value={key} key={key}>{label}</MenuItem>
                            ))}
                        </TextField>
                        {ruleType === "discount" && (
                            <TextField label="Minimum Discount %" type="number" variant="outlined" size="small" value={minDiscountPercent} onChange={(e) => setMinDiscountPercent(Number(e.target.value))} sx={{ maxWidth: 200 }} />
                        )}
                        {ruleType === "rating" && (
                            <TextField label="Minimum Rating" type="number" variant="outlined" size="small" inputProps={{ min: 0, max: 5, step: 0.5 }} value={minRating} onChange={(e) => setMinRating(Number(e.target.value))} sx={{ maxWidth: 200 }} />
                        )}
                    </div>
                )}

                <div className="flex flex-col sm:flex-row gap-4">
                    <TextField label="Max Products Shown" type="number" variant="outlined" size="small" value={limit} onChange={(e) => setLimit(Number(e.target.value))} sx={{ maxWidth: 200 }} />
                    <TextField label="Display Order" type="number" variant="outlined" size="small" value={order} onChange={(e) => setOrder(Number(e.target.value))} helperText="Lower numbers show first on the homepage" sx={{ maxWidth: 200 }} />
                </div>

                <FormControlLabel
                    control={<Checkbox checked={active} onChange={(e) => setActive(e.target.checked)} />}
                    label="Active (visible on homepage)"
                />

                <FormControlLabel
                    control={<Checkbox checked={showOnWebsite} onChange={(e) => setShowOnWebsite(e.target.checked)} />}
                    label="Show on Website"
                />

                <FormControlLabel
                    control={<Checkbox checked={showOnApp} onChange={(e) => setShowOnApp(e.target.checked)} />}
                    label="Show on Customer App"
                />

                <div className="flex justify-end">
                    <input form="productsectionform" type="submit" className="bg-primary-orange uppercase px-6 py-3 text-white font-medium rounded shadow hover:shadow-lg cursor-pointer" value={isEditMode ? "Save Changes" : "Create Section"} />
                </div>

            </form>
        </>
    );
};

export default ProductSectionForm;
